import React from 'react';
import { Link } from 'react-router-dom';
import { PHONE_DISPLAY, PHONE_TEL, SITE_URL } from '../../constants/brand';
import type { ServiceDef } from '../../data/services';
import { CORE_CITIES, hasServiceCityPage } from '../../data/cities';
import { OUTDOOR_SPECIALTY_SLUGS } from '../../data/outdoorSpecialties';
import { NAV_SERVICES, SERVICES } from '../../data/services';
import { locationPath, ROUTES, serviceCityPath, servicePath } from '../../enum/routes';
import CtaBand from '../CtaBand';
import FaqSection from '../FaqSection';
import Seo from '../Seo';

export default function ServicePageTemplate({ service }: { service: ServiceDef }) {
  const path = servicePath(service.slug);
  const isOutdoorFamily = service.slug === 'outdoor-living' || OUTDOOR_SPECIALTY_SLUGS.includes(service.slug);
  const related = (
    isOutdoorFamily
      ? SERVICES.filter(
          (s) =>
            s.slug !== service.slug &&
            (OUTDOOR_SPECIALTY_SLUGS.includes(s.slug) || s.slug === 'outdoor-living' || ['kitchen-remodeling', 'bathroom-remodeling', 'adu-construction'].includes(s.slug))
        )
      : NAV_SERVICES.filter((s) => s.slug !== service.slug)
  ).slice(0, 6);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: service.faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.metaDescription,
    provider: {
      '@type': 'Organization',
      name: 'Epic Home Pros',
      url: SITE_URL,
      telephone: PHONE_TEL,
    },
    areaServed: CORE_CITIES.map((c) => ({
      '@type': 'City',
      name: c.name,
      containedInPlace: { '@type': 'State', name: 'Texas' },
    })),
    url: `${SITE_URL}${path}`,
  };

  return (
    <>
      <Seo
        title={service.metaTitle}
        description={service.metaDescription}
        path={path}
        jsonLd={[serviceSchema, faqSchema]}
      />

      <section className="relative isolate flex min-h-[60vh] w-full items-center overflow-hidden md:min-h-[68vh]">
        <img alt={service.heroImageAlt} className="absolute inset-0 h-full w-full object-cover" src={service.heroImage} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/45 to-black/70" aria-hidden="true" />
        <div className="container-page relative z-10 py-20 text-white">
          <div className="max-w-3xl">
            <div className="label-eyebrow !text-white/90">{service.eyebrow}</div>
            <h1 className="h-display mt-4 text-white drop-shadow">{service.heroTitle}</h1>
            <p className="mt-5 max-w-2xl text-base text-white/90 sm:text-lg">{service.heroLead}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link className="btn-primary" to={ROUTES.QUOTE}>
                Get a Free Quote
              </Link>
              <a
                href={`tel:${PHONE_TEL}`}
                className="btn-secondary border-white bg-white/10 text-white hover:bg-white hover:text-forest"
              >
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl space-y-14">
            <section className="space-y-4">
              <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{service.introTitle}</h2>
              {service.introParagraphs.map((p) => (
                <p key={p.slice(0, 40)} className="leading-relaxed text-ink/80">
                  {p}
                </p>
              ))}
            </section>
            <section className="space-y-4">
              <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{service.projectTypesTitle}</h2>
              <ul className="list-disc space-y-2 pl-5 text-ink/80 marker:text-forest">
                {service.projectTypes.map((item) => (
                  <li key={item} className="leading-relaxed">
                    {item}
                  </li>
                ))}
              </ul>
            </section>
            <section className="space-y-4">
              <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{service.whyTitle}</h2>
              <ul className="list-disc space-y-2 pl-5 text-ink/80 marker:text-forest">
                {service.whyItems.map((item) => (
                  <li key={item} className="leading-relaxed">
                    {item}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      </section>

      {service.slug === 'outdoor-living' && (
        <section className="bg-cream py-16 sm:py-20">
          <div className="container-page">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">Outdoor living services</h2>
            <p className="mt-3 max-w-2xl text-ink/75">
              Detailed pages for decks, covered patios, pergolas, and outdoor kitchens - the same outdoor service set homeowners look for when comparing Texas builders.
            </p>
            <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {SERVICES.filter((s) => OUTDOOR_SPECIALTY_SLUGS.includes(s.slug)).map((s) => (
                <li key={s.slug}>
                  <Link
                    className="card-hover block h-full rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
                    to={servicePath(s.slug)}
                  >
                    <span className="font-serif text-xl text-ink">{s.name}</span>
                    <span className="mt-2 block text-sm text-ink/70">{s.heroLead}</span>
                    <span className="mt-3 block text-sm font-semibold text-forest">View {s.shortName} details →</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {service.cost && (
        <section className="bg-cream py-16 sm:py-20">
          <div className="container-page">
            <div className="mx-auto max-w-4xl">
              <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{service.cost.title}</h2>
              <p className="mt-4 leading-relaxed text-ink/80">{service.cost.intro}</p>
              <div className="mt-8 overflow-x-auto">
                <table className="w-full min-w-[32rem] text-left text-sm">
                  <thead>
                    <tr className="border-b border-black/10 text-xs uppercase tracking-wider text-ink/60">
                      <th className="py-3 pr-4 font-semibold">Remodel tier</th>
                      <th className="py-3 pr-4 font-semibold">Typical cost</th>
                      <th className="py-3 font-semibold">What it includes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {service.cost.rows.map((row) => (
                      <tr key={row.tier} className="odd:bg-white even:bg-forest-tint/40">
                        <td className="py-3 pr-4 font-medium">{row.tier}</td>
                        <td className="py-3 pr-4">{row.cost}</td>
                        <td className="py-3 text-ink/75">{row.includes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className={service.cost ? 'py-16 sm:py-20' : 'bg-cream py-16 sm:py-20'}>
        <div className="container-page">
          <div className="mx-auto max-w-4xl space-y-6">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{service.processTitle}</h2>
            <ol className="space-y-4">
              {service.processSteps.map((step) => (
                <li key={step.label} className="rounded-lg border border-black/5 bg-white p-5 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-forest">{step.label}</div>
                  <p className="mt-2 text-ink/80">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="container-page">
          <div className="mb-10">
            <div className="label-eyebrow">{service.galleryEyebrow}</div>
            <h2 className="h-section mt-3">{service.galleryTitle}</h2>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {service.gallery.map((img) => (
              <div key={img.src} className="img-zoom overflow-hidden rounded-lg">
                <img className="img-zoom-target aspect-[4/3] w-full object-cover" alt={img.alt} src={img.src} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{service.shortName} projects by city</h2>
          <p className="mt-3 max-w-2xl text-ink/75">
            Explore local pages for Austin, Dallas, Houston, and San Antonio - or browse all service areas.
          </p>
          <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CORE_CITIES.map((city) => {
              const to = hasServiceCityPage(service.slug, city.slug)
                ? serviceCityPath(service.slug, city.slug)
                : locationPath(city.slug);
              return (
                <li key={city.slug}>
                  <Link
                    className="card-hover block rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
                    to={to}
                  >
                    <span className="font-serif text-xl text-ink">
                      {city.name}, {city.state}
                    </span>
                    <span className="mt-2 block text-sm font-medium text-forest">
                      {hasServiceCityPage(service.slug, city.slug)
                        ? `${service.name} in ${city.name} →`
                        : `Remodeling in ${city.name} →`}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
          <p className="mt-6 text-sm">
            <Link className="font-medium text-forest hover:text-forest-hover hover:underline" to={ROUTES.SERVICE_AREAS}>
              View all service areas
            </Link>
          </p>
        </div>
      </section>

      <section className="bg-forest-tint py-16 sm:py-20">
        <div className="container-page">
          <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">Related services</h2>
          <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {related.map((s) => (
              <li key={s.slug}>
                <Link
                  className="block rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
                  to={servicePath(s.slug)}
                >
                  <span className="font-serif text-lg text-ink">{s.name}</span>
                  <span className="mt-1 block text-sm text-forest">Learn more →</span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-ink/70">
            Also see{' '}
            {CORE_CITIES.map((c, i) => (
              <React.Fragment key={c.slug}>
                {i > 0 && (i === CORE_CITIES.length - 1 ? ', and ' : ', ')}
                <Link className="font-medium text-forest hover:underline" to={locationPath(c.slug)}>
                  {c.name}
                </Link>
              </React.Fragment>
            ))}
            .
          </p>
        </div>
      </section>

      <FaqSection items={service.faqs} />
      <CtaBand title={service.ctaTitle} lead={service.ctaLead} />
    </>
  );
}
